import React from 'react';
import PropTypes from 'prop-types';
import LoadingElement from '~/components/Layout/LoadingElement';
import ScanConfigurationSearch from '../ScanConfigurationSearch';
import ScanConfigurationsList from '../ScanConfigurationsList';
import ScannerConfigurationForm from '../ScannerConfigurationForm';
import {
  ITEMS_PER_PAGE,
  ScannerStatusEnum,
  POLLING_INTERVAL_MS
} from '../../types/commonTypes';
import styles from './AgentScannersView.css';

// Statuses that trigger automatic polling for updates (using Set for O(1) lookup)
const POLLING_STATUSES = new Set([
  ScannerStatusEnum.QUEUED.key.toUpperCase(),
  ScannerStatusEnum.RUNNING.key.toUpperCase(),
  ScannerStatusEnum.IMPORTING.key.toUpperCase(),
  ScannerStatusEnum.DELETING.key.toUpperCase()
]);

/**
 * Checks if any scanner in the list has a status that requires polling
 * @param {Array} scanners - List of scanner configurations
 * @returns {boolean} - True if polling is needed
 */
const shouldPollScanners = (scanners) => {
  if (!scanners || !Array.isArray(scanners) || scanners.length === 0) {
    return false;
  }

  return scanners.some((scanner) => {
    const state = scanner?.state?.toUpperCase();

    return state && POLLING_STATUSES.has(state);
  });
};

/**
 * Field mappings for each scenario (backend field name → frontend field name)
 * This is the reverse of what the form uses when submitting
 */
const SCENARIO_FIELD_MAPPINGS = {
  'amazon-bedrock:access-key': {
    keyId: 'accessKeyId',
    keySecret: 'secretAccessKey',
    region: 'awsRegion'
  },
  'vertex-ai:service-account': {
    serviceAccountEmail: 'serviceAccountEmail',
    projectId: 'projectId',
    privateKey: 'privateKey',
    location: 'location'
  },
  'azure-openai:oauth': {
    tenantId: 'tenantId',
    clientId: 'clientId',
    clientSecret: 'clientSecret',
    scope: 'scope'
  }
};

/**
 * Maps API authScheme values to form authMethod values
 * API returns: 'apiKey', 'Bearer', 'oauth2-client-credentials'
 * Form expects: 'access-key', 'service-account', 'oauth'
 */
const mapAuthSchemeToAuthMethod = (authScheme, targetSystemId) => {
  // Map based on authScheme
  const schemeMap = {
    apiKey: 'access-key',
    Bearer: 'service-account',
    'oauth2-client-credentials': 'oauth'
  };

  // Also map based on target system ID as backup
  const targetSystemMap = {
    'b5e33248-1145-465b-935f-20ac88e15fab': 'access-key', // Amazon Bedrock
    '44a30473-605d-4ab4-b693-ac7fdaabd184': 'service-account', // Vertex AI
    'ebca1e8f-aeaa-4753-9fcb-18efd9060866': 'oauth' // Azure OpenAI
  };

  return schemeMap[authScheme] || targetSystemMap[targetSystemId] || authScheme;
};

/**
 * Maps target system ID to form target system value
 * API returns UUID, form expects: 'amazon-bedrock', 'vertex-ai', 'azure-openai'
 * or 'godaddy-ans'
 */
const mapTargetSystemIdToValue = (targetSystemId) => {
  const targetSystemMap = {
    'b5e33248-1145-465b-935f-20ac88e15fab': 'amazon-bedrock',
    '44a30473-605d-4ab4-b693-ac7fdaabd184': 'vertex-ai',
    'ebca1e8f-aeaa-4753-9fcb-18efd9060866': 'azure-openai',
    'd4f8a2c1-7b3e-4f9d-a1c6-8e5f2b9d0a3c': 'godaddy-ans'
  };

  return targetSystemMap[targetSystemId] || targetSystemId;
};

/**
 * Maps backend auth parameter field names to frontend field names
 * @param {Object} backendParams - Parameters with backend field names
 * @param {string} targetSystem - Target system value (e.g., 'amazon-bedrock')
 * @param {string} authMethod - Auth method value (e.g., 'access-key')
 * @returns {Object} Parameters with frontend field names
 */
const mapBackendFieldsToFrontend = (
  backendParams,
  targetSystem,
  authMethod
) => {
  // Validate input - if not a valid object, return empty object
  if (
    !backendParams ||
    typeof backendParams !== 'object' ||
    Array.isArray(backendParams)
  ) {
    return {};
  }

  const scenarioKey = `${targetSystem}:${authMethod}`;
  const fieldMapping = SCENARIO_FIELD_MAPPINGS[scenarioKey];

  if (!fieldMapping) {
    // No mapping available, return as-is
    return backendParams;
  }

  const frontendParams = {};

  Object.entries(backendParams).forEach(([backendKey, value]) => {
    const frontendKey = fieldMapping[backendKey] || backendKey;

    frontendParams[frontendKey] = value;
  });

  return frontendParams;
};

const AgentScannersView = ({
  scanConfigurations = [],
  isFetchingScanConfigurations = false,
  route,
  getPath,
  onScanConfigurationClick,
  onEditScanner,
  onFormClose = () => {},
  ScanConfigurationsListComponent = ScanConfigurationsList,
  organizationId,
  ownerId,
  onCreateScanner,
  onUpdateScanner,
  onLoadScanners,
  context,
  isAgentScannerGoDaddyEnabled
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedFilterStatus, setSelectedFilterStatus] = React.useState(null);
  const [selectedFilterTarget, setSelectedFilterTarget] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);

  // Derive showForm directly from route instead of using state
  const showForm = React.useMemo(
    () => route?.isAdd || route?.isEdit || false,
    [route?.isAdd, route?.isEdit]
  );

  const shouldPoll = shouldPollScanners(scanConfigurations);
  const isLoading =
    isFetchingScanConfigurations && scanConfigurations.length === 0;

  // Auto-polling for scanner list updates when any scanner has an active status
  // (QUEUED, RUNNING, IMPORTING, or DELETING)
  React.useEffect(() => {
    // Disable polling when:
    // - No scanners need polling (all in final states)
    // - Missing required dependencies (organizationId, onLoadScanners)
    // - Form is open (showForm=true): user is creating/editing a scanner,
    //   so updating the background list is unnecessary and could cause confusion
    if (!shouldPoll || !organizationId || !onLoadScanners || showForm) {
      return undefined;
    }

    // Set up polling interval to refresh scanners list
    const pollIntervalId = setInterval(() => {
      onLoadScanners(organizationId);
    }, POLLING_INTERVAL_MS);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(pollIntervalId);
    };
  }, [shouldPoll, organizationId, onLoadScanners, showForm]);

  // Determine if we're in edit mode and get the scanner ID from the route
  const editingScannerId = route?.isEdit ? route?.scannerId : null;

  const filteredScanConfigurations = React.useMemo(() => {
    let filtered = scanConfigurations;

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      filtered = filtered.filter(
        (config) =>
          config.name?.toLowerCase()?.includes(term) ||
          config.connection?.targetSystem?.name
            ?.toLowerCase()
            ?.includes(term) ||
          config.state?.toLowerCase()?.includes(term)
      );
    }

    // Apply status filter
    if (selectedFilterStatus) {
      filtered = filtered.filter(
        (config) => config.state?.trim()?.toLowerCase() === selectedFilterStatus
      );
    }

    // Apply target system filter
    if (selectedFilterTarget) {
      filtered = filtered.filter(
        (config) => config.connection?.targetSystem?.id === selectedFilterTarget
      );
    }

    return filtered;
  }, [
    scanConfigurations,
    searchTerm,
    selectedFilterStatus,
    selectedFilterTarget
  ]);

  const handleSearch = React.useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleFilterStatusChange = React.useCallback((filter) => {
    setSelectedFilterStatus(filter);
    setCurrentPage(1);
  }, []);

  const handleFilterTargetChange = React.useCallback((filter) => {
    setSelectedFilterTarget(filter);
    setCurrentPage(1);
  }, []);

  const handlePageChange = React.useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle form submission - calls API via Redux actions
  /**
   * Handles scanner form submission (create or update)
   * @param {Object} scannerData - Scanner configuration data from the form
   * @requires organizationId - Organization ID (from props)
   * @requires onCreateScanner - Redux action to create scanner (from props)
   * @requires onUpdateScanner - Redux action to update scanner (from props)
   * @requires onLoadScanners - Redux action to reload scanner list (from props)
   */
  const handleFormSubmit = React.useCallback(
    async (scannerData) => {
      // Validate required parameters
      if (!organizationId) {
        setSubmitError('Organization ID is required');

        return;
      }

      if (!scannerData) {
        setSubmitError('Scanner data is required');

        return;
      }

      if (editingScannerId && !onUpdateScanner) {
        setSubmitError('Update scanner function is not available');

        return;
      }

      if (!editingScannerId && !onCreateScanner) {
        setSubmitError('Create scanner function is not available');

        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        if (editingScannerId) {
          // Update existing scanner
          await onUpdateScanner(organizationId, editingScannerId, scannerData);
        } else {
          // Create new scanner
          await onCreateScanner(organizationId, scannerData);
        }

        // No need to reload the list - Redux reducer already updates it optimistically
        // The create/update actions dispatch SUCCESS actions that update state immediately

        if (onFormClose) {
          onFormClose();
        }
      } catch (error) {
        setSubmitError(
          error?.message || 'Failed to save scanner configuration'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      organizationId,
      editingScannerId,
      onCreateScanner,
      onUpdateScanner,
      onFormClose
    ]
  );

  const handleFormCancel = React.useCallback(() => {
    if (onFormClose) {
      onFormClose();
    }
  }, [onFormClose]);

  const handleRunNow = React.useCallback(
    (scannerId) => {
      // TODO: Implement run now functionality
      // Validation needed: organizationId and scannerId must be provided
      if (!organizationId || !scannerId) {
        // Future implementation will call API to trigger scanner execution
      }
      // Future implementation will call API to trigger scanner execution
    },
    [organizationId]
  );

  const handleDeleteScanner = React.useCallback(() => {
    // TODO: Implement delete functionality
  }, []);

  // Get scanner data for editing
  const editingScannerData = React.useMemo(() => {
    if (!editingScannerId) return null;

    const scanner = scanConfigurations.find((s) => s.id === editingScannerId);

    if (!scanner) return null;

    // Transform scanner data to flatten connection information
    // The API returns connection data nested in connection.authParameters (as JSON string)
    // but the form expects it flattened at the top level
    const targetSystemId = scanner.connection?.targetSystem?.id;
    const targetSystemValue = mapTargetSystemIdToValue(targetSystemId);
    const authScheme = scanner.connection?.authScheme;
    const authMethodValue = mapAuthSchemeToAuthMethod(
      authScheme,
      targetSystemId
    );

    const transformedData = {
      ...scanner,
      // Map target system from UUID to form value (e.g., 'amazon-bedrock')
      targetSystem: targetSystemValue || scanner.targetSystem,
      target: targetSystemValue || scanner.target,
      // Map auth scheme from API value to form value (e.g., 'apiKey' -> 'access-key')
      authMethod: authMethodValue || scanner.authMethod,
      method: authMethodValue || scanner.method
    };

    // Parse and flatten authParameters if they exist
    if (scanner.connection?.authParameters) {
      try {
        const backendAuthParams =
          typeof scanner.connection.authParameters === 'string'
            ? JSON.parse(scanner.connection.authParameters)
            : scanner.connection.authParameters;

        // Map backend field names to frontend field names
        const frontendAuthParams = mapBackendFieldsToFrontend(
          backendAuthParams,
          targetSystemValue,
          authMethodValue
        );

        // Merge auth parameters into the top level
        Object.assign(transformedData, frontendAuthParams);
      } catch (error) {
        // If parsing fails, continue with what we have
        // Error can be logged during development if needed
      }
    }

    // Parse and flatten email notification settings
    // API returns:
    // - notificationEnabled (boolean) for the notification toggle state
    // - emails as a stringified JSON array (e.g., '["email1@test.com","email2@test.com"]')
    // Form expects:
    // - emailNotificationsEnabled (boolean)
    // - recipientEmails (array)

    // Set notification enabled state from backend field
    if (scanner.notificationEnabled !== undefined) {
      transformedData.emailNotificationsEnabled = scanner.notificationEnabled;
    }

    // Parse recipient emails if they exist
    if (scanner.emails) {
      try {
        const emailArray =
          typeof scanner.emails === 'string'
            ? JSON.parse(scanner.emails)
            : scanner.emails;

        if (Array.isArray(emailArray)) {
          transformedData.recipientEmails = emailArray;
        }
      } catch (error) {
        // If parsing fails, continue with defaults
        // Error can be logged during development if needed
      }
    }

    return transformedData;
  }, [editingScannerId, scanConfigurations]);

  // Show form view
  if (showForm) {
    return (
      <div className={styles.formContainer}>
        <ScannerConfigurationForm
          mode={editingScannerId ? 'edit' : 'add'}
          initialData={editingScannerData}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          organizationId={organizationId}
          ownerId={ownerId}
          isSubmitting={isSubmitting}
          submitError={submitError}
          isAgentScannerGoDaddyEnabled={isAgentScannerGoDaddyEnabled}
        />
      </div>
    );
  }

  // Show list view
  return (
    <div>
      <div aria-label="Search">
        <ScanConfigurationSearch
          onSearch={handleSearch}
          onClear={handleClearSearch}
          onFilterStatusChange={handleFilterStatusChange}
          onFilterTargetChange={handleFilterTargetChange}
          searchTerm={searchTerm}
          selectedFilterStatus={selectedFilterStatus}
          selectedFilterTarget={selectedFilterTarget}
          context={context}
        />
      </div>
      <section
        aria-live="assertive"
        aria-label="Agent Scanners"
        className={styles.searchResults}
      >
        {/* Show loading only on initial load (when no data exists yet) */}
        <LoadingElement
          display-if={isLoading}
          message="Searching for Scan Configurations"
        />
        {/* Show list if we have data (even during background refresh) */}
        <ScanConfigurationsListComponent
          display-if={!isLoading}
          id="scan-configurations-results"
          scanConfigurations={filteredScanConfigurations}
          getPath={getPath}
          onScanConfigurationClick={onScanConfigurationClick}
          currentPage={currentPage}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={handlePageChange}
          organizationId={organizationId}
          onLoadScanConfigurations={onLoadScanners}
          onRunNowClick={handleRunNow}
          onEditScannerClick={onEditScanner}
          onDeleteScannerClick={handleDeleteScanner}
        />
      </section>
    </div>
  );
};

AgentScannersView.propTypes = {
  scanConfigurations: PropTypes.arrayOf(PropTypes.object),
  isFetchingScanConfigurations: PropTypes.bool,
  route: PropTypes.shape({
    isAdd: PropTypes.bool,
    isEdit: PropTypes.bool,
    scannerId: PropTypes.string
  }),
  getPath: PropTypes.func,
  onScanConfigurationClick: PropTypes.func,
  onEditScanner: PropTypes.func,
  onFormClose: PropTypes.func,
  ScanConfigurationsListComponent: PropTypes.elementType,
  organizationId: PropTypes.string,
  ownerId: PropTypes.string,
  onCreateScanner: PropTypes.func,
  onUpdateScanner: PropTypes.func,
  onLoadScanners: PropTypes.func,
  context: PropTypes.object,
  isAgentScannerGoDaddyEnabled: PropTypes.bool
};

// Export for testing
export { shouldPollScanners, POLLING_STATUSES, POLLING_INTERVAL_MS };

export default AgentScannersView;
